import React, { useCallback, useEffect, useMemo, useState } from "react"
import { StyleSheet, Text, View } from "react-native"
import { useFocusEffect } from "@react-navigation/native"
import * as amplitude from "@amplitude/analytics-react-native"
import FilledButtonM from "@common/components/button/FilledButtonM"
import {
    FUNNEL_TITLE_FONT_SIZE,
    FUNNEL_TITLE_LINE_HEIGHT,
} from "@common/components/funnel/FunnelQuestionScreen"
import FunnelFadeUp from "@common/components/funnel/FunnelFadeUp"
import FunnelStepHeader from "@common/components/funnel/FunnelStepHeader"
import FunnelStepLayout from "@common/components/funnel/FunnelStepLayout"
import FunnelTutorSearch from "@common/components/funnel/FunnelTutorSearch"
import { useFunnelTutorReveal } from "@common/components/funnel/useFunnelTutorReveal"
import { theme } from "@common/theme/theme"
import { elide } from "@common/utils/activationSuiteDisplay"
import SummerEyebrow from "../summer/components/SummerEyebrow"
import SummerHelpBanner from "../summer/components/SummerHelpBanner"
import SummerTutorsContent from "../summer/components/SummerTutorsContent"
import { useInscriptionFunnel } from "./context/InscriptionFunnelContext"
import { useInscriptionFunnelGuard } from "./hooks/useInscriptionFunnelGuard"
import { useInscriptionLead } from "./hooks/useInscriptionLead"
import { useInscriptionSubjects } from "./hooks/useInscriptionSubjects"
import { inscriptionFunnelService } from "./services/inscriptionFunnelService"
import {
    INSCRIPTION_STEPS,
    INSCRIPTION_TUTORS_SUBTITLE,
    formatSlotsShort,
} from "./constants/inscriptionContent"

/**
 * Étape « aha moment masteur » — la recherche est RACONTÉE : trois critères
 * qui se cochent avec les mots de l'élève, le dernier seulement quand le
 * matching a répondu ; le vivier s'allume dans la scène visage par visage,
 * puis le compte monte et les cartes entrent. Pas de spinner.
 *
 * Le parent ne choisit PAS un masteur ici : le sous-titre le dit et le CTA
 * dit « Continuer », pas « Réserver » — devant des visages, « réserver »
 * se lisait comme « réserver l'un d'eux », et le parent cherchait comment.
 *
 * Étape NON bloquante : en cas d'erreur, de réponse vide ou de contexte pas
 * encore hydraté, repli gracieux (titre neutre, scène de référence) et le CTA
 * route TOUJOURS vers le paiement. Jamais de cul-de-sac.
 */
const EYEBROW = "🎒 Cours réguliers"

const InscriptionTutorsScreen = ({ navigation }) => {
    const { state } = useInscriptionFunnel()
    const { nextParams } = useInscriptionFunnelGuard()
    const { funnelStateId: leadStateId } = useInscriptionLead()
    // Les noms des matières pour raconter la recherche ; sans eux, la ligne
    // reste vague plutôt que fausse.
    const subjectsCatalogue = useInscriptionSubjects(state.studentClass)

    const [loading, setLoading] = useState(true)
    const [matching, setMatching] = useState(null)
    const [failed, setFailed] = useState(false)
    // La recherche mise en scène tient l'écran jusqu'à ce que ses critères
    // soient cochés ET que le matching ait répondu — jamais un spinner infini.
    const [searching, setSearching] = useState(true)
    // Vivier dans la scène, compte qui monte, cartes retenues : la même
    // révélation que le stage.
    const { showcase, shownCount, scene } = useFunnelTutorReveal({
        loading,
        matching,
        searching,
    })

    const firstname = state.studentFirstname || "votre enfant"
    const funnelStateId = Number(leadStateId)
    const hasValidStateId = Number.isInteger(funnelStateId) && funnelStateId > 0
    const subjects = (state.subjects || []).map(Number)
    const hasMatchingPayload =
        hasValidStateId && subjects.length > 0 && !!state.studentClass

    useEffect(() => {
        amplitude.track("inscription_funnel_tutors_viewed", {
            funnel_state_id: leadStateId,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useFocusEffect(
        useCallback(() => {
            // Contexte froid : la garde redirige vers inscription/resume.
            if (!hasValidStateId) return
            if (matching || failed) return
            // Panier incomplet (cas limite) → repli direct, pas d'appel voué au 422.
            if (!hasMatchingPayload) {
                setFailed(true)
                setLoading(false)
                return
            }

            let cancelled = false
            setLoading(true)
            setFailed(false)
            inscriptionFunnelService
                .getInscriptionMatchingTutors(
                    { stateId: funnelStateId },
                    {
                        subjects,
                        studentClass: state.studentClass,
                        startDate: state.startDate,
                    }
                )
                .then((res) => {
                    if (cancelled) return
                    const payload = res?.data?.data ?? res?.data ?? null
                    if (!payload || !(payload.count > 0)) {
                        setFailed(true)
                        return
                    }
                    setMatching(payload)
                })
                .catch(() => {
                    if (cancelled) return
                    // Erreur (réseau / 4xx / 5xx) : on NE bloque PAS le funnel.
                    setFailed(true)
                })
                .finally(() => {
                    if (!cancelled) setLoading(false)
                })
            return () => {
                cancelled = true
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [hasValidStateId, hasMatchingPayload])
    )

    const goToCheckout = () => {
        amplitude.track("inscription_funnel_tutors_completed", {
            funnel_state_id: leadStateId,
        })
        navigation.navigate("inscription/checkout", nextParams)
    }

    // Les critères, avec les mots de l'élève : son niveau et son profil (le
    // mot reste vague à dessein — il couvre les spécificités exprimées comme
    // celles qui ne le sont pas), ses matières, ses disponibilités. Jamais une
    // promesse sur le niveau visé.
    const criteria = useMemo(() => {
        const lines = [
            state.studentClass
                ? `Adaptés au niveau ${state.studentClass} et au profil ${elide(firstname)}`
                : `Adaptés au niveau et au profil ${elide(firstname)}`,
        ]
        const nameById = new Map(
            (subjectsCatalogue || []).map((s) => [Number(s.id), s.name])
        )
        const names = subjects.map((id) => nameById.get(id)).filter(Boolean)
        lines.push(
            names.length
                ? `Spécialistes ${names.length > 1 ? "de " + names.slice(0, -1).join(", ") + " et " + names[names.length - 1] : "de " + names[0]}`
                : "Spécialistes de ses matières"
        )
        const when = formatSlotsShort(state.slots || [])
        lines.push(
            when ? `Disponibles ${when}` : "Disponibles sur vos créneaux"
        )
        return lines
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        state.studentClass,
        state.slots,
        subjectsCatalogue,
        subjects.join(),
        firstname,
    ])

    return (
        <FunnelStepLayout
            scene={scene}
            contentContainerStyle={styles.content}
            header={
                <FunnelStepHeader
                    step={INSCRIPTION_STEPS.tutors}
                    total={INSCRIPTION_STEPS.total}
                    onBack={() => navigation.goBack()}
                />
            }
            footer={
                <View style={styles.footer}>
                    <FilledButtonM
                        label="Continuer"
                        onPress={goToCheckout}
                        // Le CTA attend la fin de la révélation, et laisse les
                        // écritures amont atterrir avant d'ouvrir le paiement.
                        disabled={loading || searching}
                        color={
                            loading || searching
                                ? theme.colors.lightMore
                                : theme.colors.primary
                        }
                        style={{ width: "100%", marginBottom: 0 }}
                    />
                </View>
            }
        >
            {searching ? (
                <>
                    {/* L'écran garde son en-tête pendant la recherche : les
                        étapes qui se cochent ne flottent pas dans le vide. */}
                    <SummerEyebrow label={EYEBROW} />
                    <Text style={styles.title}>
                        On cherche les masteurs {elide(firstname)}
                    </Text>
                    <FunnelTutorSearch
                        criteria={criteria}
                        found={!loading}
                        onDone={() => setSearching(false)}
                    />
                </>
            ) : (
                <FunnelFadeUp>
                    <SummerTutorsContent
                        firstname={firstname}
                        cardAvatarSize={72}
                        stackAvatarSize={44}
                        subtitle={INSCRIPTION_TUTORS_SUBTITLE(firstname)}
                        // Ce que le parent gagne, c'est de savoir ce qui s'est
                        // passé — un compte-rendu après chaque séance.
                        thirdGuarantee={{
                            icon: "clipboard-check",
                            label: "Un compte-rendu après chaque séance",
                        }}
                        count={shownCount}
                        total={matching?.total}
                        tutors={showcase}
                        moreAvatars={matching?.more_avatars}
                        failed={failed}
                        eyebrowLabel={EYEBROW}
                    />
                </FunnelFadeUp>
            )}
            <SummerHelpBanner
                step={INSCRIPTION_STEPS.tutors}
                trackingEvent="inscription_funnel_help_clicked"
            />
        </FunnelStepLayout>
    )
}

const styles = StyleSheet.create({
    content: { paddingBottom: 24 },
    title: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: FUNNEL_TITLE_FONT_SIZE,
        lineHeight: FUNNEL_TITLE_LINE_HEIGHT,
        color: theme.colors.primaryDark || theme.colors.primary,
        marginBottom: 14,
    },
    footer: {
        paddingVertical: 14,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },
})

export default InscriptionTutorsScreen
